import { useId, useRef, useState, type ReactNode } from "react";
import { ApiError } from "@/api/client";
import { ErrorBanner, ToolbarButton } from "@/components/ui";
import { useRevealProblems } from "@/components/form";
import {
  PAPER_SIZES,
  validateProfileForm,
  type PaperSize,
  type ProfileField,
  type ProfileFormValues,
} from "@/features/printers/profileValues";

const inputClass =
  "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand aria-invalid:border-red-400";

/** Every profile field with the API's limits; a 422's details are shown on the field they name. */
export function ProfileForm({
  initial,
  isUnchanged,
  submitLabel,
  pendingLabel,
  pending,
  error,
  onEdit,
  onSubmit,
  onCancel,
  autoFocus = false,
}: {
  initial: ProfileFormValues;
  isUnchanged?: (values: ProfileFormValues) => boolean;
  submitLabel: string;
  pendingLabel: string;
  pending: boolean;
  error: unknown;
  onEdit?: () => void;
  onSubmit: (values: ProfileFormValues) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const [values, setValues] = useState(initial);
  const clientErrors = validateProfileForm(values);
  const serverDetails = error instanceof ApiError && error.status === 422 ? error.details : [];
  const serverErrors: Partial<Record<string, string>> = {};
  const otherDetails: string[] = [];
  for (const detail of serverDetails) {
    if (detail.field && detail.field in initial) serverErrors[detail.field] = detail.issue;
    else otherDetails.push(detail.field ? `${detail.field}: ${detail.issue}` : (detail.issue ?? ""));
  }
  const formRef = useRef<HTMLFormElement>(null);
  const reveal = useRevealProblems<ProfileField>(formRef);
  const problem = (field: ProfileField) => reveal.shown(field, clientErrors[field]) ?? serverErrors[field];
  const set = <K extends ProfileField>(field: K, value: ProfileFormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    onEdit?.();
  };
  const invalid = Object.keys(clientErrors).length > 0;
  const unchanged = isUnchanged?.(values) ?? false;

  const text = (
    field: ProfileField,
    label: string,
    extra: { type?: string; step?: string; min?: number; max?: number; placeholder?: string; autoFocus?: boolean } = {},
  ) => (
    <Field label={label} problem={problem(field)}>
      {(props) => (
        <input
          {...props}
          {...extra}
          value={values[field] as string}
          onChange={(e) => set(field, e.target.value)}
          onBlur={() => reveal.touch(field)}
          className={inputClass}
        />
      )}
    </Field>
  );

  return (
    <form
      ref={formRef}
      noValidate
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (invalid) {
          reveal.submitted();
          return;
        }
        if (!unchanged && !pending) onSubmit(values);
      }}
    >
      <div className="grid gap-3 md:grid-cols-3">
        {text("name", "Name", { autoFocus })}
        {text("vendor", "Vendor")}
        {text("model", "Model")}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Paper size" problem={problem("paper_size")}>
          {(props) => (
            <select
              {...props}
              value={values.paper_size}
              onChange={(e) => set("paper_size", e.target.value as PaperSize)}
              onBlur={() => reveal.touch("paper_size")}
              className={inputClass}
            >
              {PAPER_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          )}
        </Field>
        {text("offset_x_mm", "Offset X (mm)", { type: "number", step: "0.01", min: -50, max: 50 })}
        {text("offset_y_mm", "Offset Y (mm)", { type: "number", step: "0.01", min: -50, max: 50 })}
      </div>
      <p className="text-xs text-zinc-500">
        Offsets shift the whole page: positive X moves it right, positive Y down. Leave a margin empty to keep the template's.
      </p>
      <div className="grid gap-3 md:grid-cols-4">
        {text("margin_top_mm", "Top margin (mm)", { type: "number", step: "0.01", min: 0, max: 100, placeholder: "Template's" })}
        {text("margin_right_mm", "Right margin (mm)", { type: "number", step: "0.01", min: 0, max: 100, placeholder: "Template's" })}
        {text("margin_bottom_mm", "Bottom margin (mm)", { type: "number", step: "0.01", min: 0, max: 100, placeholder: "Template's" })}
        {text("margin_left_mm", "Left margin (mm)", { type: "number", step: "0.01", min: 0, max: 100, placeholder: "Template's" })}
      </div>
      <div className="grid gap-3 md:grid-cols-3 md:items-end">
        {text("default_sub_batch_size", "Default sub-batch size", { type: "number", step: "1", min: 1, max: 500 })}
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={values.preprinted_stock}
            onChange={(e) => set("preprinted_stock", e.target.checked)}
          />
          Pre-printed stock
        </label>
      </div>
      <p className="text-xs text-zinc-500">Pre-printed stock carries the background, so spools leave it out.</p>
      {text("ipp_uri", "IPP printer address", { placeholder: "ipp://printer.example/ipp/print" })}
      <Field label="Vendor specs" problem={problem("vendor_specs")}>
        {(props) => (
          <textarea
            {...props}
            rows={3}
            value={values.vendor_specs}
            onChange={(e) => set("vendor_specs", e.target.value)}
            onBlur={() => reveal.touch("vendor_specs")}
            className={inputClass}
          />
        )}
      </Field>

      <ErrorBanner error={error} />
      {otherDetails.length > 0 ? (
        <ul className="list-disc pl-5 text-sm text-red-700">
          {otherDetails.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={unchanged || pending}
          className="rounded-md border border-brand-strong bg-brand-strong px-3 py-2 text-sm font-medium text-white transition hover:bg-brand-stronger disabled:cursor-not-allowed disabled:opacity-50"
        >
          {pending ? pendingLabel : submitLabel}
        </button>
        {onCancel ? (
          <ToolbarButton disabled={pending} onClick={onCancel}>
            Cancel
          </ToolbarButton>
        ) : null}
      </div>
    </form>
  );
}

function Field({
  label,
  problem,
  children,
}: {
  label: string;
  problem?: string;
  children: (props: { id: string; "aria-invalid"?: true; "aria-describedby"?: string }) => ReactNode;
}) {
  const id = useId();
  const problemId = `${id}-problem`;
  return (
    <div className="text-sm">
      <label htmlFor={id} className="font-medium text-zinc-700">
        {label}
      </label>
      {children({ id, ...(problem ? { "aria-invalid": true, "aria-describedby": problemId } : {}) })}
      {problem ? (
        <p id={problemId} className="mt-1 text-xs text-red-700">
          {problem}
        </p>
      ) : null}
    </div>
  );
}
